// Dependencies
import React from 'react';
import { values } from 'lodash';
import { useState, useEffect } from 'react';
import { useInterval } from 'react-use';
import styled, { css } from 'styled-components';

// Types
import { Types, Components } from '../../types';
import { Helpers } from '../../utils';

const config = {
	delay: 200, // NOTE: 200ms is optimal to prevent unwanted "double" behaviour
	actions: {
		decrement: {
			position: 'left',
			label: '-', 
			value: -1
		},
		increment: {
			position: 'right',
			label: '+',
			value: 1
		},
	}
};

export const QuantitySelect = ({quantity, onChange: handleChange}: Components.QuantitySelect) => {
	const [isUpdating, setIsUpdating] = useState<Types.Object<any> | undefined>(undefined);

	const validateQuantity = (oldQuantity?: number, newQuantity?: number) => {

		// Check against minimum quantity
		if ( (newQuantity || quantity.selected) < (quantity.min >= 1 ? quantity.min : 1 ) ) {
			return oldQuantity || quantity.min;
		}

		// Check against maximum quantity
		if ( quantity.max && (newQuantity || quantity.selected) > quantity.max ) {
			return oldQuantity || quantity.max;
		}

		return newQuantity || quantity.selected;

	}

	const setQuantity = ({action, value}: {action?: Types.Object<any>, value?: number}) => {
		const oldQuantity   = quantity.selected;
		let newQuantity     = oldQuantity;
		
		// Calculate 
		newQuantity = value
			? value
			: newQuantity + ( action?.value || 0);
		
		// Validate and return valid one
		newQuantity = validateQuantity(oldQuantity, newQuantity);            

		// Update quantity state if it diffs
		oldQuantity !== newQuantity && handleChange(newQuantity)

	}

	const handleQuantityUpdate = React.useCallback((action: any = undefined) => {
		setQuantity({action});        
		setIsUpdating(action);        
	}, [isUpdating, setQuantity]) 
	
	// Re-validate selected quantity 
	// whenever min/max values change
	useEffect(() => setQuantity({ value: validateQuantity()}), [quantity.min, quantity.max])
	
	// Update (increment/decrement) current quantity every 200ms 
	// while one of button is being pressed 
	useInterval(() => setQuantity({action: isUpdating}), isUpdating ? config.delay : null);

	const { min, max, selected} = quantity;

	return ( 
		<QuantitySelectWrapper>
			<QuantityButton action={config.actions.decrement} onUpdate={handleQuantityUpdate} />
			<QuantityInput onChange={(value: number) => setQuantity({value})} {...{min, max, value: selected || min}} />
			<QuantityButton action={config.actions.increment} onUpdate={handleQuantityUpdate} />
		</QuantitySelectWrapper>
	)
}


export const QuantitySelectWrapper = styled.div<any>(
({ theme: { general, components: {quantitySelect: style} } }) => css`
	display: flex;
	max-width: ${style.maxWidth ?? 6.25}em;
	width: 100%;
	height: 100%;
	max-height: 2.25em;
	margin-top: auto;
`);


export const StyledQuantityInput = styled.input<any>(
({ theme: { general, components: {quantitySelect: style} } }) => css`
	
	min-width: 2em !important;
	padding: 0.25em !important;
	margin: 0 !important;
	
	height: 100% !important;

	color: ${style.textColor} !important;
	border: ${style.borderWidth}em solid ${style.borderColor} !important;
	border-radius: 0 !important;
	border-left: none !important;
	border-right: none !important;

	background: inherit !important;
	text-align: center !important;
	
	transition: all ${general.transition.duration} ${general.transition.timingFunction} !important;

	// TODO: Mixin
	&:focus-within {
		box-shadow: 0 0 0 .1em rgba(0, 0, 0, .15) !important;
	}
	
	@media (-moz-touch-enabled: 1), (hover: none), (pointer: coarse) {
		&:focus-within {
			box-shadow: unset !important;
		}
		&:active {
			box-shadow: 0 0 0 .1em rgba(0, 0, 0, .15) !important;
		}
	}
	
	&:active,
	&:focus,
	&:hover {
		outline: none;
		background-color: rgb(255, 255, 255) !important;
	}

`);


const QuantityInput = ({onChange: handleChange, ...props}: any) => {
	const inputRef = React.useRef<any>(null);
	const [isFocused, setIsFocused] = React.useState<boolean>(false);

	const handleFocus = () => {
		setIsFocused(true);
		inputRef.current.select();
	}

	const handleBlur = () => {
		setIsFocused(false);
	}

	const handleInput = () => {
		handleChange( parseInt( inputRef.current?.value )  )
	};

	const handleClick = () => {
		requestAnimationFrame(() => {
			isFocused && inputRef.current.select();
		})
	}

	return (
		<StyledQuantityInput 
			type="text" 
			// name="quantity" 
			inputMode="numeric"
			autoComplete="off"
			ref={inputRef}
			onClick={handleClick}
			onFocus={handleFocus}
			onBlur={handleBlur}
			onInput={handleInput}
			{...props} 
		/>
	)
}


export const QuantityButton = ({action, onUpdate: handleUpdate}: any) => {
	const buttonRef = React.useRef<any>(null);

	// TODO: Diff mouse/touch device to prevent unwanted "double" behaviour while registering both events (onMouseDown/onTouchStartCapture)

	const handleBeforeUpdate = (event: any) => {

		switch ( event.type ) {
			case 'keypress':
				if ( event.key === 'Enter' ) handleUpdate(action);
				break;

			case 'touchstart':
				buttonRef.current.addEventListener('touchend', handleBeforeUpdate);
				buttonRef.current.addEventListener('touchcancel', handleBeforeUpdate);
				handleUpdate(action);
				break;

			case 'mousedown':

				// NOTE: Quickfix: Do not trigger
				if ( 'ontouchstart' in window ) {
					break;
				}

				buttonRef.current.addEventListener('mouseup', handleBeforeUpdate);
				buttonRef.current.addEventListener('mouseleave', handleBeforeUpdate);
				handleUpdate(action);
				break;

			case 'touchend':
			case 'touchcancel':
			case 'mouseup':
			case 'mouseleave':
				buttonRef.current.removeEventListener(event.type, handleBeforeUpdate);
				handleUpdate(undefined);
				break

		}

	}    

	const buttonProps = React.useMemo(() => ({
		ref: buttonRef,
		position: action.position,
		onKeyPress: handleBeforeUpdate,
		onMouseDown: handleBeforeUpdate,
		onTouchStartCapture: handleBeforeUpdate
	}), [action, handleUpdate]);

	return (
		<StyledQuantityButton {...buttonProps}>
			{action.label}
		</StyledQuantityButton>
	)
	
} 


export const StyledQuantityButton = styled.button<any>(
({ position, theme: { general, components: {quantitySelect: style} } }) => css`

	outline: none;
	cursor: pointer;
	user-select: none;

	margin: 0 !important;
	
	min-width: 1em;
	flex: 1 1 100%;
	
	height: 100%;
	
	padding: .5em;
	
	border: ${style.borderWidth}em solid ${style.borderColor};
	
	${`border-top-${position}-radius: ${Helpers.getFormattedValue(style.borderRadius ?? 0)} !important;`}
	${`border-bottom-${position}-radius: ${Helpers.getFormattedValue(style.borderRadius ?? 0)} !important;`}
	
	text-align: center;

	background: ${style.buttonBackground || 'inherit'};
	color: ${style.buttonTextColor || 'inherit'};
	
	transition: all ${general.transition.duration} ${general.transition.timingFunction} !important;

	&:active,
	&:hover {
		background: ${style.hoverButtonBackground};
		color: ${style.hoverButtonTextColor};
		border: ${style.borderWidthHover ?? style.borderWidth}em solid ${style.borderColorHover ?? style.borderColor};
	}

	// TODO: Mixin
	&:focus-within {
		box-shadow: 0 0 0 .1em rgba(0, 0, 0, .15) !important;
	}
	
	@media (-moz-touch-enabled: 1), (hover: none), (pointer: coarse) {
		&:hover {
			background: ${style.buttonBackground || 'inherit'};
			color: ${style.buttonTextColor || 'inherit'};
			border: ${style.borderWidth}em solid ${style.borderColor};
			${`border-top-${position}-radius: ${Helpers.getFormattedValue(style.borderRadius ?? 0)} !important;`}
			${`border-bottom-${position}-radius: ${Helpers.getFormattedValue(style.borderRadius ?? 0)} !important;`}
		}
		&:focus-within {
			box-shadow: unset !important;
		}
		&:active {
			& {
				transition: none !important;
			}
			background: ${style.hoverButtonBackground};
			color: ${style.hoverButtonTextColor};
			border: ${style.borderWidthHover ?? style.borderWidth}em solid ${style.borderColorHover ?? style.borderColor};
			box-shadow: 0 0 0 .1em rgba(0, 0, 0, .15) !important;
		}
	}

`);